// src/components/SetupWizard.js
import React, { useState, useEffect } from 'react';
import { Modal, Steps, Button, Input, Alert, Spin, Typography, Space, Card, Tag, Row, Col } from 'antd';
import { 
  RocketOutlined, 
  ApiOutlined, 
  CheckCircleOutlined, 
  SafetyCertificateOutlined,
  ThunderboltOutlined,
  ExperimentOutlined,
  ArrowRightOutlined,
  DollarOutlined
} from '@ant-design/icons';
import axios from 'axios';
import '../styles/SetupWizard.css';

const { Title, Text, Paragraph } = Typography;

// Animated background component
const AnimatedNodeBackground = () => {
  return (
    <div className="animated-nodes-bg">
      <div className="floating-node node-1">
        <div className="node-content">🤖</div>
      </div>
      <div className="floating-node node-2">
        <div className="node-content">🧠</div>
      </div>
      <div className="floating-node node-3">
        <div className="node-content">⚡</div>
      </div>
      <div className="floating-node node-4">
        <div className="node-content">🚀</div>
      </div>
      <div className="floating-node node-5">
        <div className="node-content">💡</div>
      </div>
      <div className="floating-node node-6">
        <div className="node-content">✨</div>
      </div>
      
      {/* Connection lines */}
      <svg className="connection-lines" width="100%" height="100%">
        <line className="connection-line" x1="20%" y1="20%" x2="50%" y2="40%" />
        <line className="connection-line" x1="50%" y1="40%" x2="80%" y2="30%" />
        <line className="connection-line" x1="30%" y1="60%" x2="70%" y2="70%" />
      </svg>
    </div>
  );
};

const SetupWizard = ({ onComplete, isLicenseActive = true }) => {
  const [visible, setVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [apiKey, setApiKey] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [isKeyValid, setIsKeyValid] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [existingOpenRouterKey, setExistingOpenRouterKey] = useState('');
  const [hasCompletedWizard, setHasCompletedWizard] = useState(false);

  useEffect(() => {
    // Check if setup is needed
    const checkSetupStatus = async () => {
      try {
        // For Pro version, check if license is active (if prop is provided)
        const isProVersion = process.env.REACT_APP_VERSION !== 'lite';
        
        if (isProVersion && isLicenseActive !== undefined && !isLicenseActive) {
          console.log('Pro version but license not active, skipping setup wizard');
          return;
        }
        
        const response = await axios.get(
          `${window.wpAiWorkflowsSettings.root}wp-ai-workflows/v1/settings`,
          {
            headers: {
              'X-WP-Nonce': window.wpAiWorkflowsSettings.nonce
            }
          }
        );
        
        const hasCompletedSetup = response.data?.setup_completed;
        
        // Check if they have an existing OpenRouter key
        if (response.data?.openrouter_api_key && response.data.openrouter_api_key.includes('*')) {
          setExistingOpenRouterKey(response.data.openrouter_api_key);
          setApiKey(response.data.openrouter_api_key);
          setIsKeyValid(true);
        }
        
        // Only show wizard if setup not completed AND wizard wasn't already completed this session
        if (!hasCompletedSetup && !hasCompletedWizard) {
          setTimeout(() => setVisible(true), 500);
        }
      } catch (error) {
        console.error('Error checking setup status:', error);
      }
    };

    checkSetupStatus();
  }, [hasCompletedWizard, isLicenseActive]);

  const validateApiKey = async (key) => {
    // If it's a masked key (contains asterisks), skip validation
    if (key && key.includes('*')) {
      setIsKeyValid(true);
      return true;
    }

    if (!key || key.length < 20) {
      setValidationError('Please enter a valid API key');
      return false;
    }

    setIsValidating(true);
    setValidationError('');

    try {
      // Test the API key with OpenRouter
      const response = await fetch('https://openrouter.ai/api/v1/models', {
        headers: {
          'Authorization': `Bearer ${key}`,
          'HTTP-Referer': window.location.origin,
          'X-Title': 'WP AI Workflows'
        }
      });

      if (response.ok) {
        setIsKeyValid(true);
        return true;
      } else {
        setValidationError('Invalid API key. Please check and try again.');
        return false;
      }
    } catch (error) {
      setValidationError('Failed to validate API key. Please try again.');
      return false;
    } finally {
      setIsValidating(false);
    }
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      // If they have an existing key and haven't changed it, skip validation
      if (apiKey && apiKey.includes('*')) {
        setCurrentStep(currentStep + 1);
        return;
      }
      
      // For new keys, validate them
      if (apiKey && apiKey.length >= 10) {
        const isValid = await validateApiKey(apiKey);
        if (!isValid) return;
      }
    }
    
    if (currentStep < 2) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleSkip = async () => {
    setHasCompletedWizard(true);
    try {
      // Mark setup as completed even if skipped
      await axios.put(
        `${window.wpAiWorkflowsSettings.root}wp-ai-workflows/v1/settings`,
        {
          setup_completed: '1' // Use string '1' for WordPress compatibility
        },
        {
          headers: {
            'X-WP-Nonce': window.wpAiWorkflowsSettings.nonce,
            'Content-Type': 'application/json'
          }
        }
      );
    } catch (error) {
      console.error('Failed to save skip status:', error);
    }
    
    setVisible(false);
  };

  const handleCancel = () => {
    // Don't mark as complete if user cancels by clicking outside
    setVisible(false);
  };

  const handleFinish = async () => {
    setIsSaving(true);
    setHasCompletedWizard(true);
    
    try {
      // First get current settings to preserve existing API keys
      const currentSettings = await axios.get(
        `${window.wpAiWorkflowsSettings.root}wp-ai-workflows/v1/settings`,
        {
          headers: {
            'X-WP-Nonce': window.wpAiWorkflowsSettings.nonce
          }
        }
      );

      // Get current selected models or default to empty array
      let selectedModels = currentSettings.data.selected_models || [];
      
      // Add openrouter to selected models if not already there
      if (!selectedModels.includes('openrouter')) {
        selectedModels.push('openrouter');
      }

      // Only update the OpenRouter key if it's not a masked value
      let settingsToUpdate = {
        setup_completed: '1', // Use string '1' for WordPress compatibility
        selected_models: selectedModels
      };

      // Only update API key if it's new (not masked)
      if (apiKey && !apiKey.includes('*')) {
        settingsToUpdate.openrouter_api_key = apiKey;
      }

      // Save the settings
      await axios.put(
        `${window.wpAiWorkflowsSettings.root}wp-ai-workflows/v1/settings`,
        settingsToUpdate,
        {
          headers: {
            'X-WP-Nonce': window.wpAiWorkflowsSettings.nonce,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Redirect to workflow builder with template and setup flag
      if (onComplete) {
        onComplete();
      } else {
        const baseUrl = window.location.href.split('?')[0];
        window.location.href = `${baseUrl}?page=wp-ai-workflows&action=builder&template=rss-to-blog&from_setup=true`;
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      setValidationError('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const steps = [
    {
      title: 'Welcome',
      icon: <RocketOutlined />
    },
    {
      title: 'Connect AI',
      icon: <ApiOutlined />
    },
    {
      title: 'Get Started',
      icon: <CheckCircleOutlined />
    }
  ];

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className="wizard-step-content welcome-step">
            <div className="welcome-icon">
              <RocketOutlined style={{ fontSize: 48, color: '#1890ff' }} />
            </div>
            <Title level={2}>Welcome to AI Workflow Automation!</Title>
            <Paragraph className="welcome-subtitle">
              Let's set up your first AI automation in just 2 minutes
            </Paragraph>
            
            <div className="feature-grid">
              <div className="feature-item">
                <div className="feature-icon-wrapper">
                  <ThunderboltOutlined style={{ fontSize: 28, color: '#52c41a' }} />
                </div>
                <div className="feature-content">
                  <h4>Instant Setup</h4>
                  <p>Start with free AI models</p>
                </div>
              </div>
              
              <div className="feature-item">
                <div className="feature-icon-wrapper">
                  <SafetyCertificateOutlined style={{ fontSize: 28, color: '#1890ff' }} />
                </div>
                <div className="feature-content">
                  <h4>Your Keys, Your Control</h4>
                  <p>Secure & private</p>
                </div>
              </div>
              
              <div className="feature-item">
                <div className="feature-icon-wrapper">
                  <ExperimentOutlined style={{ fontSize: 28, color: '#722ed1' }} />
                </div>
                <div className="feature-content">
                  <h4>100+ AI Models</h4>
                  <p>OpenAI, Claude, Gemini & more</p>
                </div>
              </div>
            </div>

            <Space className="step-actions" size="large">
              <Button size="large" onClick={handleSkip}>
                Skip Setup
              </Button>
              <Button type="primary" size="large" icon={<ArrowRightOutlined />} onClick={handleNext}>
                Get Started
              </Button>
            </Space>
          </div>
        );

      case 1:
        return (
          <div className="wizard-step-content api-step">
            <div className="api-header">
              <Space direction="vertical" align="center" size={8}>
                <ApiOutlined style={{ fontSize: 40, color: '#1890ff' }} />
                <Title level={3} style={{ margin: 0 }}>Connect to AI Models</Title>
                <Text type="secondary">With OpenRouter, one key unlocks 100+ AI models</Text>
                <Text type="secondary">Use your own API key and start using AI with Free models like Google Gemma 3</Text>
              </Space>
            </div>

            <div className="api-main-content">
              {/* Show existing key message if applicable */}
              {existingOpenRouterKey && existingOpenRouterKey.includes('*') && (
                <Alert
                  type="success"
                  message={
                    <Space>
                      <CheckCircleOutlined />
                      <span>You already have an API key configured</span>
                    </Space>
                  }
                  description="Continue with your existing key or enter a new one to replace it."
                  showIcon={false}
                  className="existing-key-alert"
                />
              )}

              {/* Main API Input */}
              <Card className="api-key-card" bordered={false}>
                <Space direction="vertical" style={{ width: '100%' }} size={16}>
                  <div>
                    <Title level={5} style={{ marginBottom: 8, fontSize: 16 }}>
                      OpenRouter API Key
                    </Title>
                    <Input.Password
                      size="large"
                      placeholder="sk-or-v1-..."
                      value={apiKey}
                      onChange={(e) => {
                        const newKey = e.target.value;
                        setApiKey(newKey);
                        setValidationError('');
                        // Enable continue button if key is entered
                        if (newKey && newKey.length > 10) {
                          setIsKeyValid(true);
                        } else {
                          setIsKeyValid(false);
                        }
                      }}
                      status={validationError ? 'error' : isKeyValid ? 'success' : ''}
                      suffix={isKeyValid ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : null}
                    />
                    {validationError && (
                      <Text type="danger" style={{ fontSize: 12 }}>{validationError}</Text>
                    )}
                  </div>

                  <Button 
                    type="link" 
                    href="https://openrouter.ai/keys" 
                    target="_blank"
                    icon={<ArrowRightOutlined />}
                    style={{ padding: 0 }}
                  >
                    Get your free API key
                  </Button>
                </Space>
              </Card>

              <div className="additional-models-note">
                <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
                  💡 Want OpenAI, Perplexity, or LlamaIndex?
                </Text>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  Add them later in your Settings page
                </Text>
              </div>
            </div>

            <Space className="step-actions" size="large">
              <Button size="large" onClick={() => setCurrentStep(0)}>
                Back
              </Button>
              <Button 
                type="primary" 
                size="large" 
                loading={isValidating}
                disabled={!apiKey || apiKey.length < 10 || isValidating}
                onClick={handleNext}
              >
                {isValidating ? 'Validating...' : (apiKey.includes('*') ? 'Continue with Existing Key' : 'Continue')}
              </Button>
            </Space>
          </div>
        );

      case 2:
        return (
          <div className="wizard-step-content success-step">
            <div className="success-header">
              <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a' }} />
              <Title level={2} style={{ margin: '12px 0 4px' }}>You're All Set!</Title>
              <Paragraph className="success-subtitle" style={{ margin: 0 }}>
                Let's create your first AI automation workflow
              </Paragraph>
            </div>

            <Card className="first-workflow-card" bordered={false}>
              <Space direction="horizontal" align="start" size={16}>
                <ExperimentOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                <div style={{ flex: 1 }}>
                  <Title level={4} style={{ margin: '0 0 4px 0' }}>
                    Your First Workflow: RSS to Blog Post
                  </Title>
                  <Paragraph style={{ margin: '0 0 8px 0', fontSize: 13 }}>
                    We've prepared a template that:
                  </Paragraph>
                  <Row gutter={[16, 8]}>
                    <Col xs={24} sm={12}>
                      <Space direction="vertical" size={4}>
                        <Text style={{ fontSize: 13 }}>• Fetches content from RSS feeds</Text>
                        <Text style={{ fontSize: 13 }}>• Rewrites with AI enhancement</Text>
                      </Space>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Space direction="vertical" size={4}>
                        <Text style={{ fontSize: 13 }}>• Creates SEO-optimized posts</Text>
                        <Space>
                          <Text style={{ fontSize: 13 }}>• Uses</Text>
                          <Tag color="green">FREE</Tag>
                          <Text style={{ fontSize: 13 }}>Gemma model</Text>
                        </Space>
                      </Space>
                    </Col>
                  </Row>
                </div>
              </Space>
            </Card>

            <Alert
              type="success"
              message="No charges until you upgrade"
              description="This workflow uses free AI models. Upgrade to premium models anytime for better quality."
              showIcon
              style={{ marginBottom: 20 }}
            />

            <Button 
              type="primary" 
              size="large" 
              block
              loading={isSaving}
              icon={<RocketOutlined />}
              onClick={handleFinish}
              className="finish-button"
            >
              Create My First AI Workflow
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width="90%"
      style={{ maxWidth: '900px' }}
      centered
      className="setup-wizard-modal"
      closable={true}
      maskClosable={false}
    >
      <AnimatedNodeBackground />
      <div className="setup-wizard-container">
        <Steps 
          current={currentStep} 
          items={steps}
          className="wizard-steps"
          size="small"
        />
        {renderStepContent()}
      </div>
    </Modal>
  );
};

export default SetupWizard;